import { createSlice } from "@reduxjs/toolkit";
import { status } from "../../_constants/common";
import {
    createDealProposal,
    getInfluencerDealProposals,
    withdrawDealProposal,
    createInfluencerProposalDraft,
    updateInfluencerProposalDraft,
    togglePauseDeal,
    fetchInfluencerDrafts,
    deleteInfluencerDraft,
} from "./InfluencerDealProposalThunk";

const initialState = {
    dealProposals: [],
    pagination: {
        total: 0,
        page: 1,
        // limit: 20,
        totalPages: 0,
    },
    statusCounts: null, // Status counts from API for tabs
    isLoading: false,
    createProposalStatus: status.IDLE,
    withdrawLoading: false,
    pauseLoading: false,
    error: null,
    draftsList: [],
    draftsLoading: false,
    draftsError: null,
    // Persisted draft progress — survives navigation and page reload via redux-persist.
    // imagePreview (base64) is stored for display; File objects cannot be serialized.
    draft: {
        draftId: null,
        currentStep: 1,
        selectedDealTypeId: null,
        formSnapshot: null,
        imagePreview: null,
        existingImageUrl: null,
        saveStatus: null,
    },
};

const influencerDealProposalSlice = createSlice({
    name: "influencerDealProposal",
    initialState,
    reducers: {
        clearCreateProposalStatus: (state) => {
            state.createProposalStatus = status.IDLE;
        },
        setProposalDraftSnapshot: (state, { payload }) => {
            state.draft = { ...state.draft, ...payload };
        },
        clearProposalDraftSnapshot: (state) => {
            state.draft = {
                draftId: null,
                currentStep: 1,
                selectedDealTypeId: null,
                formSnapshot: null,
                imagePreview: null,
                existingImageUrl: null,
                saveStatus: null,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // Get Influencer Deal Proposals
            .addCase(getInfluencerDealProposals.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getInfluencerDealProposals.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.dealProposals = payload.proposals || [];
                state.pagination = payload.pagination || initialState.pagination;
                state.statusCounts = payload.statusCounts || null; // Store status counts
            })
            .addCase(getInfluencerDealProposals.rejected, (state, { payload }) => {
                state.isLoading = false;
                state.error = payload;
            })

            // Create Deal Proposal
            .addCase(createDealProposal.pending, (state) => {
                state.createProposalStatus = status.IN_PROGRESS;
            })
            .addCase(createDealProposal.fulfilled, (state, action) => {
                state.createProposalStatus = status.SUCCESS;
                // If the submitted deal was linked to a draft, remove it from the
                // drafts list immediately so it doesn't show as draft any more.
                const submittedDraftId = action.meta?.arg?.draftId;
                if (submittedDraftId) {
                    state.draftsList = state.draftsList.filter((d) => d.id !== submittedDraftId);
                }
            })
            .addCase(createDealProposal.rejected, (state, { payload }) => {
                state.createProposalStatus = status.FAILURE;
                state.error = payload;
            })

            // Withdraw Deal Proposal
            .addCase(withdrawDealProposal.pending, (state) => {
                state.withdrawLoading = true;
            })
            .addCase(withdrawDealProposal.fulfilled, (state, { payload }) => {
                state.withdrawLoading = false;
                // Remove withdrawn proposal from list or update its status
                state.dealProposals = state.dealProposals.map((proposal) =>
                    proposal.id === payload.proposal.id
                        ? { ...proposal, status: "cancelled" }
                        : proposal
                );
            })
            .addCase(withdrawDealProposal.rejected, (state, { payload }) => {
                state.withdrawLoading = false;
                state.error = payload;
            })

            // Create Influencer Proposal Draft
            .addCase(createInfluencerProposalDraft.pending, (state) => {
                state.draft.saveStatus = status.IN_PROGRESS;
            })
            .addCase(createInfluencerProposalDraft.fulfilled, (state, { payload }) => {
                state.draft.saveStatus = status.SUCCESS;
                if (payload?.id) {
                    state.draft.draftId = payload.id;
                }
            })
            .addCase(createInfluencerProposalDraft.rejected, (state) => {
                state.draft.saveStatus = status.FAILURE;
            })

            // Update Influencer Proposal Draft
            .addCase(updateInfluencerProposalDraft.pending, (state) => {
                state.draft.saveStatus = status.IN_PROGRESS;
            })
            .addCase(updateInfluencerProposalDraft.fulfilled, (state) => {
                state.draft.saveStatus = status.SUCCESS;
            })
            .addCase(updateInfluencerProposalDraft.rejected, (state) => {
                state.draft.saveStatus = status.FAILURE;
            })

            // Fetch Influencer Drafts
            .addCase(fetchInfluencerDrafts.pending, (state) => {
                state.draftsLoading = true;
                state.draftsError = null;
            })
            .addCase(fetchInfluencerDrafts.fulfilled, (state, { payload }) => {
                state.draftsLoading = false;
                state.draftsList = payload?.drafts || payload || [];
            })
            .addCase(fetchInfluencerDrafts.rejected, (state, { payload }) => {
                state.draftsLoading = false;
                state.draftsError = payload;
            })

            // Delete Influencer Draft
            .addCase(deleteInfluencerDraft.fulfilled, (state, { payload }) => {
                state.draftsList = state.draftsList.filter((d) => d.id !== payload);
            })

            // Toggle Pause Deal
            .addCase(togglePauseDeal.pending, (state) => {
                state.pauseLoading = true;
            })
            .addCase(togglePauseDeal.fulfilled, (state, { payload }) => {
                state.pauseLoading = false;
                // Update isPaused flag on the matching deal in the list
                state.dealProposals = state.dealProposals.map((proposal) =>
                    proposal.deal?.id === payload.dealId
                        ? { ...proposal, deal: { ...proposal.deal, isPaused: payload.isPaused } }
                        : proposal
                );
            })
            .addCase(togglePauseDeal.rejected, (state) => {
                state.pauseLoading = false;
            });
    },
});

export const { clearCreateProposalStatus, setProposalDraftSnapshot, clearProposalDraftSnapshot } = influencerDealProposalSlice.actions;
export default influencerDealProposalSlice.reducer;
